import { Fragment, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { api } from '../api';
import { LeitorQr } from '../LeitorQr';
import { useSessao } from '../sessao';
import { Lixeira, useLixeira } from '../Lixeira';
import { data, mesAno, numero, plural, porNome, rotulo } from '../formato';
import {
  AcaoBloqueada, Aviso, Campo, Carregando, Dialogo, ErroDaApi, Escolha,
  LinkProtegido, Rn, Selo, Vazio,
} from '../ui';
import {
  FiltrosAtivos, RodapeDaLista, usePaginacao, type FiltroAtivo,
} from '../BarraDeLista';
import { Seletor } from '../Filtros';
import { MenuDaOs } from './OsMoldura';
import type { Tom } from '../ui';
import {
  Form, FormEquipamento, type Categoria,
} from './EquipamentoForms';

/**
 * Parque de equipamentos — a tela 11 do protótipo.
 *
 * É tabela e não lista de cartões de propósito. O técnico chega aqui com o
 * ensaio para começar e uma pergunta de comparação: *qual* das balanças de
 * 200 g eu posso usar hoje? Comparação se faz com as colunas alinhadas —
 * faixa embaixo de faixa, validade embaixo de validade. Cartão empilhado
 * obriga a guardar o número da linha anterior na cabeça.
 *
 * Quatro colunas e nenhuma decorativa:
 *
 *  · **faixa / resolução** — é por elas que a `RN-154` decide se o
 *    equipamento serve ao método (no LL só balança de 0,01 g);
 *  · **calibração** — a data que a `RN-151` consulta antes de deixar o
 *    equipamento entrar num ensaio;
 *  · **situação** — símbolo, palavra e cor, nessa ordem.
 */

interface Equipamento {
  id: string;
  codigo_patrimonial: string;
  categoria: string;
  categoria_nome?: string;
  familia?: string;
  nome: string;
  fabricante: string | null;
  modelo: string | null;
  numero_serie: string | null;
  localizacao: string | null;
  calibravel: boolean;
  status: string;
  data_fabricacao?: string | null;
  /* Da `vw_equipamento_apto` e da `0109`, desde 01/09/2026. */
  calibracao_ate?: string | null;
  componentes?: number;
  componentes_impedidos?: number;
  impedimento?: string | null;
  impedimento_codigo?: string | null;
  impedimento_longo?: string | null;
  /** Quantas pendências abertas, e não só a mais grave. */
  impedimentos?: number;
  /* Onda 206: a última calibração e a última aferição, e quantos dias
     faltam para a validade (conta do banco, `fn_hoje`). */
  ultima_calibracao?: string | null;
  ultima_afericao?: string | null;
  afericao_ate?: string | null;
  dias_para_vencer?: number | null;
}

/** Um sensor de dentro de uma máquina (`/equipamentos/componentes`). */
interface Sensor {
  pai_id: string;
  id: string;
  codigo_patrimonial: string;
  nome: string;
  grandeza_medida: string | null;
  faixa_min: string | null;
  faixa_max: string | null;
  faixa_unidade: string | null;
  status: string;
  calibracao_ate: string | null;
  apto: boolean | null;
  impedimento: string | null;
  numero_serie?: string | null;
  ultima_calibracao?: string | null;
  dias_para_vencer?: number | null;
}

/*
 * A lista de status que impediam uso saiu daqui em 01/09/2026.
 *
 * Ela conhecia quatro motivos; a `vw_equipamento_impedimento` conhece
 * dez, e a diferença são justamente os que não aparecem no status —
 * calibração vencida, manutenção aberta, sensor sem calibração válida.
 * Manter a lista curta ao lado da longa era garantir que uma hora as
 * duas dariam números diferentes na mesma tela. Foi o que aconteceu.
 */

/**
 * As situações que a lista oferece para filtrar.
 *
 * É o mesmo vocabulário do `TOM` abaixo, e de propósito: um filtro que
 * oferecesse uma situação que o selo não sabe pintar seria um filtro que
 * devolve linhas sem cor. A ordem é a da bancada — o que está livre
 * primeiro, o que está fora de serviço depois.
 */
const SITUACOES: { codigo: string; nome: string }[] = [
  { codigo: 'disponivel', nome: 'Disponível' },
  { codigo: 'reservado', nome: 'Reservado' },
  { codigo: 'em_uso', nome: 'Em uso' },
  { codigo: 'em_verificacao', nome: 'Em verificação' },
  { codigo: 'em_calibracao', nome: 'Em calibração' },
  { codigo: 'em_manutencao', nome: 'Em manutenção' },
  { codigo: 'fora_de_tolerancia', nome: 'Fora de tolerância' },
  { codigo: 'bloqueado', nome: 'Bloqueado' },
  { codigo: 'inativo', nome: 'Inativo' },
  { codigo: 'descartado', nome: 'Descartado' },
];

const TOM: Record<string, Tom> = {
  disponivel: 'ok',
  reservado: 'andamento',
  em_uso: 'andamento',
  em_verificacao: 'andamento',
  em_calibracao: 'andamento',
  em_manutencao: 'atencao',
  fora_de_tolerancia: 'critico',
  bloqueado: 'critico',
  inativo: 'neutro',
  descartado: 'neutro',
};

/*
 * As famílias do catálogo (`0096`).
 *
 * O parque da Testesolo tem 89 registros e dezoito categorias, e a
 * primeira pergunta de quem chega aqui quase nunca é "qual categoria":
 * é "estou procurando uma **máquina** ou uma **cápsula**". São duas
 * buscas de natureza diferente. A máquina tem certificado de calibração
 * externa e número de série; o molde, a cápsula e o anel são aferidos
 * aqui dentro, contra os padrões calibrados (balança, paquímetro,
 * trena), e o que se procura neles é a constante — a tara, a altura, o
 * diâmetro.
 *
 * Misturar as duas numa faixa única de dezoito botões obrigava a
 * percorrer o catálogo inteiro para achar "cápsula de umidade". A
 * família é o primeiro corte; a categoria continua sendo o segundo,
 * agora dentro do corte escolhido.
 *
 * A ordem é a da operação, não a alfabética: primeiro o que executa o
 * ensaio, depois o que mede, depois o que é medido.
 */
export const FAMILIAS: { codigo: string; nome: string; sub: string }[] = [
  { codigo: 'maquina', nome: 'Máquinas',
    sub: 'Prensas, triaxiais, adensamentos — calibração externa por grandeza' },
  { codigo: 'instrumento', nome: 'Instrumentos',
    sub: 'Balanças, estufas, paquímetros — medem e são calibrados' },
  { codigo: 'item_aferido', nome: 'Itens calibrados',
    sub: 'Moldes, cápsulas e anéis — calibrados aqui contra os padrões' },
  { codigo: 'componente', nome: 'Componentes',
    sub: 'Células, sensores e réguas que compõem uma máquina' },
  { codigo: 'padrao', nome: 'Padrões',
    sub: 'Os instrumentos contra os quais o laboratório afere os seus' },
];

/** Capacidade e resolução numa célula só, como a bancada lê: "200 g / 0,01 g". */
function faixaDe(e: Record<string, unknown>): string {
  const cap = e.capacidade_nominal
    ? `${numero(e.capacidade_nominal, 3, true)} ${e.capacidade_unidade ?? ''}`.trim()
    : '';
  const res = e.resolucao
    ? `${numero(e.resolucao, 4, true)} ${e.resolucao_unidade ?? ''}`.trim()
    : '';
  return [cap, res].filter(Boolean).join(' / ');
}

/**
 * Quantos dias antes a validade passa a pedir atenção. É a mesma janela
 * do relatório do parque (`DIAS_ALERTA_VENCIMENTO` na API): tela e papel
 * não podem discordar sobre o que está «vencendo».
 *
 * RN-271 (onda 350): sessenta dias, a janela das Pendências da
 * metrologia. Eram trinta aqui e no relatório, e a mesma balança «vencia
 * em breve» nas Pendências e não no parque.
 */
const DIAS_ALERTA = 60;

/**
 * Onda 350 · as faixas do filtro de validade — as mesmas da API
 * (`FAIXAS_DE_VALIDADE`, `GET /equipamentos?validade=`).
 */
const VALIDADES: { valor: string; texto: string }[] = [
  { valor: 'vencida', texto: 'Vencida' },
  { valor: 'ate30', texto: 'Vence em até 30 dias' },
  { valor: 'ate60', texto: `Vence em até ${DIAS_ALERTA} dias` },
  { valor: 'em_dia', texto: `Em dia (mais de ${DIAS_ALERTA} dias)` },
  { valor: 'sem_data', texto: 'Sem data de validade' },
];

/**
 * Onda 350 · o motivo de um travado, como o cartão dos travados o conta.
 *
 * É o `curto` da `vw_equipamento_impedimento_principal` — a mesma frase
 * da linha, da ficha e das Pendências —, menos num caso: o do sensor, que
 * traz a contagem dentro («2 sensor(es) sem calibração») e por isso
 * viraria um motivo por quantidade. Ali o motivo é um só.
 */
function motivoDe(e: Equipamento): string | null {
  if (!e.impedimento) return null;
  if (e.impedimento_codigo === 'componente_impedido') return 'sensor sem calibração';
  return e.impedimento;
}


/**
 * A validade, com o prazo escrito ao lado — e não só pintado.
 *
 * «30/09/2026» sozinho obriga quem lê a fazer a conta de cabeça. O prazo
 * aparece só quando pede ação (vencida, ou dentro da janela); validade
 * folgada é só a data, para o alerta continuar sendo alerta.
 */
function Validade({ ate, dias }: { ate?: string | null; dias?: number | null }) {
  if (!ate) return <span className="nada">—</span>;
  const d = typeof dias === 'number' ? dias : null;
  const prazo: [string, string] | null = d === null ? null
    : d < 0 ? ['critico', `vencida há ${-d} d`]
      : d <= DIAS_ALERTA ? ['atencao', d === 0 ? 'vence hoje' : `faltam ${d} d`]
        : null;
  return (
    <span className="pq-validade">
      <span className="mono">{data(ate)}</span>
      {prazo && <span className={`pq-prazo ${prazo[0]}`}>{prazo[1]}</span>}
    </span>
  );
}

/**
 * A última calibração — ou a última aferição, dita pelo nome.
 *
 * Máquina tem certificado externo; cápsula e molde são aferidos aqui
 * contra um padrão (`RN-160`). A coluna diz qual das duas é, para que a
 * data de uma cápsula não seja lida como a de um laboratório acreditado.
 */
function Ultima({ e }: { e: Equipamento }) {
  if (e.ultima_calibracao) return <span className="mono">{data(e.ultima_calibracao)}</span>;
  if (e.ultima_afericao) {
    return (
      <span className="pq-validade">
        <span className="mono">{data(e.ultima_afericao)}</span>
        <span className="pq-prazo neutro">calibração interna</span>
      </span>
    );
  }
  if (!e.calibravel) return <span className="nada">não calibrável</span>;
  return <span className="nada">sem registro</span>;
}

/** Situação: o impedimento quando há, senão o status — símbolo, palavra e cor. */
function Situacao({ e }: { e: Equipamento }) {
  if (e.impedimento) {
    return (
      <Selo tom="critico" titulo={e.impedimento_longo ?? undefined}>
        {e.impedimento}
        {/* O número só aparece quando é MAIS de uma: «impedido · 1» não
            diz nada que o selo já não diga. */}
        {(e.impedimentos ?? 0) > 1 && ` · ${e.impedimentos}`}
      </Selo>
    );
  }
  return <Selo tom={TOM[e.status] ?? 'neutro'}>{rotulo(e.status)}</Selo>;
}

export function Equipamentos({ papel = 'todos' }: { papel?: string } = {}) {
  /*
   * A família vem do endereço (onda 208): `/padroes` virou
   * `/equipamentos?familia=padrao`, e o link antigo, o favorito e a ficha
   * do padrão («Voltar») chegam na aba certa.
   */
  const [parametros, setParametros] = useSearchParams();
  const [busca, setBusca] = useState('');
  const [categoria, setCategoria] = useState('');
  const [familia, setFamiliaEstado] = useState(parametros.get('familia') ?? '');
  const setFamilia = (f: string) => {
    setFamiliaEstado(f);
    const p = new URLSearchParams(parametros);
    if (f) p.set('familia', f); else p.delete('familia');
    setParametros(p, { replace: true });
  };
  /*
   * «Padrões» é uma aba, e não mais uma tela (onda 208). Quando ela está
   * escolhida, a tela fala de padrão: o botão cadastra padrão e o
   * relatório sai com o título dos padrões.
   */
  const ePadrao = papel === 'padrao' || familia === 'padrao';
  const [lista, setLista] = useState<Equipamento[] | null>(null);
  /* A lixeira (onda 246): apagar recarrega a lista e os sensores. */
  const [apagados, setApagados] = useState(0);
  const [sensores, setSensores] = useState<Sensor[]>([]);
  /* `codigo` ou `vencimento` — a Testesolo pediu as duas em 01/09/2026. */
  const [ordem, setOrdem] = useState('codigo');
  const [soPendencia, setSoPendencia] = useState(false);
  /*
   * Duas visualizações, e cada uma responde uma pergunta diferente.
   *
   * «Ficou bacana pois é possível fazer uma consulta rápida por tipo de
   *  equipamento e ver os sensores deles. Porém deveria ter a opção de
   *  visualização por lista com filtros» — Humberto, 01/09/2026.
   *
   * AGRUPADO responde «o que temos deste tipo?». LISTA responde «quais
   * são estes aqui, e o que eu faço com eles» — e é ela que tem as caixas
   * de seleção.
   */
  const [modo, setModo] = useState<'grupo' | 'lista'>('grupo');
  const [situacao, setSituacao] = useState('');
  /* Onda 350: a faixa de validade (servidor) e o motivo do travado (tela). */
  /* Onda 350: os dois no endereço, como a família (P-LST-05) — o link
     mandado ao colega abre no mesmo recorte. */
  const [validade, setValidadeEstado] = useState(parametros.get('validade') ?? '');
  const [motivo, setMotivoEstado] = useState<string | null>(parametros.get('motivo'));
  const noEndereco = (chave: string, v: string | null) => {
    const p = new URLSearchParams(window.location.search);
    if (v) p.set(chave, v); else p.delete(chave);
    setParametros(p, { replace: true });
  };
  const setValidade = (v: string) => { setValidadeEstado(v); noEndereco('validade', v || null); };
  const setMotivo = (v: string | null) => { setMotivoEstado(v); noEndereco('motivo', v); };
  const [escolhidos, setEscolhidos] = useState<Set<string>>(new Set());
  /* Que categorias estão abertas. Recolhida é o estado normal: são 26. */
  const [abertas, setAbertas] = useState<Set<string>>(new Set());
  /* Que máquinas mostram os sensores. */
  const [comSensores, setComSensores] = useState<Set<string>>(new Set());
  const [erro, setErro] = useState<unknown>(null);
  const [criando, setCriando] = useState(false);
  const [lendoQr, setLendoQr] = useState(false);
  const { pode, eu } = useSessao();
  const ir = useNavigate();

  useEffect(() => {
    const abortar = new AbortController();
    const t = setTimeout(() => {
      const p = new URLSearchParams();
      if (busca.trim()) p.set('busca', busca.trim());
      if (categoria) p.set('categoria', categoria);
      if (situacao) p.set('status', situacao);
      if (familia) p.set('familia', familia);
      if (ordem !== 'codigo') p.set('ordem', ordem);
      if (soPendencia) p.set('pendencia', 'true');
      if (validade) p.set('validade', validade);
      p.set('papel', papel);
      const q = p.toString() ? `?${p}` : '';
      api<Equipamento[]>(`/equipamentos${q}`, { sinal: abortar.signal })
        .then((r) => { setLista(r); setErro(null); })
        .catch((e) => { if ((e as Error).name !== 'AbortError') setErro(e); });
    }, busca ? 300 : 0);
    return () => { clearTimeout(t); abortar.abort(); };
  }, [busca, categoria, familia, papel, ordem, soPendencia, situacao, validade, apagados]);

  /*
   * Os sensores vêm TODOS de uma vez, e não ao abrir cada máquina.
   *
   * São 54 linhas curtas. Buscar por máquina faria a seta demorar, e uma
   * seta que demora é uma seta que ninguém usa duas vezes.
   */
  useEffect(() => {
    if (ePadrao || (familia && familia !== 'maquina')) { setSensores([]); return undefined; }
    const abortar = new AbortController();
    api<Sensor[]>('/equipamentos/componentes', { sinal: abortar.signal })
      .then(setSensores)
      .catch(() => setSensores([]));
    return () => abortar.abort();
  }, [familia, ePadrao, apagados]);

  /*
   * As categorias vêm do CATÁLOGO (`0039`), e não mais do que já existe
   * no parque — no parque vazio não haveria categoria nenhuma para
   * escolher.
   */
  const [categorias, setCategorias] = useState<Categoria[]>([]);
  const [recarga, setRecarga] = useState(0);
  useEffect(() => {
    const abortar = new AbortController();
    // `todas=true` para quem administra: sem as inativas ele não teria
    // como reativar nenhuma.
    const q = pode('cadastro.categoria') ? '&todas=true' : '';
    api<Categoria[]>(`/equipamentos/categorias?papel=${papel}${q}`,
      { sinal: abortar.signal })
      .then(setCategorias)
      .catch(() => { /* sem catálogo, o formulário avisa e o filtro some */ });
    return () => abortar.abort();
  }, [papel, recarga, pode]);

  const nomeDaCategoria = (c: string) =>
    categorias.find((x) => x.codigo === c)?.nome ?? rotulo(c);

  /*
   * As abas e os filtros saem da MESMA lista de categorias: a conta da aba
   * é a soma dos `quantos` das categorias daquela família.
   */
  /* Onda 352 (RN-273): o filtro também em ordem alfabética. */
  const comItem = categorias.filter((c) => Number(c.quantos) > 0)
    .sort((a, b) => porNome(a.nome, b.nome));
  const quantosNaFamilia = (f: string) => comItem
    .filter((c) => (c.familia ?? 'instrumento') === f)
    .reduce((soma, c) => soma + Number(c.quantos), 0);
  const familiasPresentes = FAMILIAS
    .map((f) => ({ ...f, quantos: quantosNaFamilia(f.codigo) }))
    .filter((f) => f.quantos > 0);
  const totalNoCatalogo = familiasPresentes.reduce((s, f) => s + f.quantos, 0);

  /* Trocar de aba com uma categoria de outra família escolhida deixaria
     a tela pedindo «cápsula» dentro de «Máquinas». */
  const trocarFamilia = (f: string) => {
    const nova = familia === f ? '' : f;
    setFamilia(nova);
    if (categoria && nova) {
      const c = categorias.find((x) => x.codigo === categoria);
      if ((c?.familia ?? 'instrumento') !== nova) setCategoria('');
    }
  };

  /*
   * Quantos não podem entrar em ensaio — pela `0109`, e não pelo status.
   * O status conhece quatro motivos; a visão conhece dez — e a diferença
   * são justamente os que ninguém vê: calibração vencida, manutenção
   * aberta, sensor sem calibração.
   */
  const impedidos = (lista ?? []).filter((e) => e.impedimento).length;

  /*
   * Os indicadores do topo (onda 206). Saem da MESMA lista que a tabela
   * desenha — um número no cartão que a lista não confirma é a `D-29` de
   * novo. «Vencendo» é a validade dentro da janela, e só entre os que
   * ainda podem ser usados: o vencido já está contado como impedido.
   */
  const indicadores = useMemo(() => {
    const l = lista ?? [];
    const vencendo = l.filter((e) => !e.impedimento
      && typeof e.dias_para_vencer === 'number'
      && e.dias_para_vencer >= 0 && e.dias_para_vencer <= DIAS_ALERTA).length;
    return { total: l.length, aptos: l.length - impedidos, vencendo };
  }, [lista, impedidos]);

  /*
   * Onda 350 · «o parque diz por quê».
   *
   * «84 sem poder entrar em ensaio» sozinho manda a pessoa abrir ficha por
   * ficha para descobrir o que está errado. O cartão passa a contar os
   * travados pelo motivo mais grave de cada um (a mesma visão da ficha e
   * das Pendências), do mais frequente para o menos — e cada motivo é um
   * filtro.
   */
  const motivos = useMemo(() => {
    const conta = new Map<string, number>();
    for (const e of lista ?? []) {
      const m = motivoDe(e);
      if (m) conta.set(m, (conta.get(m) ?? 0) + 1);
    }
    return [...conta.entries()]
      .map(([texto, n]) => ({ texto, n }))
      .sort((a, b) => b.n - a.n || a.texto.localeCompare(b.texto, 'pt-BR'));
  }, [lista]);

  /* O motivo escolhido some quando a lista nova já não tem ninguém com ele. */
  useEffect(() => {
    if (motivo && lista && !motivos.some((m) => m.texto === motivo)) setMotivo(null);
  }, [motivo, motivos, lista]);

  /** O que a tabela desenha: a lista do servidor, cortada pelo motivo. */
  const visiveis = useMemo(
    () => (motivo ? (lista ?? []).filter((e) => motivoDe(e) === motivo) : lista ?? []),
    [lista, motivo]);

  /*
   * ─── O agrupamento ────────────────────────────────────────────────
   *
   * As categorias são a ESTRUTURA da lista: cada uma é uma linha
   * recolhível. Recolhida é o estado normal. Agrupar aqui, e não no
   * servidor, porque o servidor já mandou a lista ordenada como a pessoa
   * pediu, e agrupar é só ler essa ordem em blocos.
   */
  const grupos = useMemo(() => {
    const porCategoria = new Map<string, Equipamento[]>();
    for (const e of visiveis) {
      const k = e.categoria;
      if (!porCategoria.has(k)) porCategoria.set(k, []);
      porCategoria.get(k)!.push(e);
    }
    const ordemFamilia = new Map(FAMILIAS.map((f, i) => [f.codigo, i]));
    return [...porCategoria.entries()]
      .map(([codigo, itens]) => ({
        codigo,
        nome: nomeDaCategoria(codigo),
        familia: itens[0].familia ?? 'instrumento',
        itens,
        travados: itens.filter((e) => e.impedimento).length,
      }))
      /* Família e, dentro dela, ordem alfabética (onda 352, RN-273): «o
         parque de equipamentos também deveria estar em ordem alfabética» —
         Humberto, 23/10/2026. Substitui a ordem da casa da 350; antes dela
         era a QUANTIDADE de itens, e a categoria mudava de lugar a cada
         molde cadastrado. */
      .sort((a, b) => (ordemFamilia.get(a.familia) ?? 9)
        - (ordemFamilia.get(b.familia) ?? 9)
        || porNome(a.nome, b.nome));
  }, [visiveis, categorias]);

  /** Os sensores de cada máquina, prontos para a seta. */
  const sensoresDe = useMemo(() => {
    const m = new Map<string, Sensor[]>();
    for (const c of sensores) {
      if (!m.has(c.pai_id)) m.set(c.pai_id, []);
      m.get(c.pai_id)!.push(c);
    }
    return m;
  }, [sensores]);

  /* Buscar ABRE o que casou; sem filtro, volta ao que a pessoa abriu. */
  const filtrando = busca.trim() !== '' || categoria !== '' || soPendencia
    || validade !== '' || motivo !== null;
  const aberta = (c: string) => filtrando || abertas.has(c);
  const alternar = (c: string) => setAbertas((antes) => {
    const s2 = new Set(antes);
    if (s2.has(c)) s2.delete(c); else s2.add(c);
    return s2;
  });
  const alternarSensores = (id: string) => setComSensores((antes) => {
    const s2 = new Set(antes);
    if (s2.has(id)) s2.delete(id); else s2.add(id);
    return s2;
  });
  const travadosVisiveis = visiveis.filter((e) => e.impedimento).length;
  const todasAbertas = grupos.length > 0 && grupos.every((g) => abertas.has(g.codigo));
  const abrirOuFecharTodas = () => setAbertas(
    todasAbertas ? new Set() : new Set(grupos.map((g) => g.codigo)));

  /* Anel e cápsula não têm faixa: a coluna do meio muda com a família. */
  const colunaDoMeio = (fam: string) =>
    (fam === 'maquina' || fam === 'instrumento' || fam === 'padrao'
      ? 'faixa' : 'local');

  /* O relatório sai com a família e a categoria da tela (onda 206); a
     validade, a situação e o motivo ainda não (vão com a onda das
     remessas, espec. 11 §5.7). */
  const caminhoRelatorio = (() => {
    const p = new URLSearchParams({ papel: ePadrao ? 'padrao' : papel });
    if (familia && !ePadrao) p.set('familia', familia);
    if (categoria) p.set('categoria', categoria);
    p.set('pdf', '1');
    return `/equipamentos/relatorio-parque?${p}`;
  })();

  /*
   * Onda 350 · os filtros ativos, removíveis um a um (P-LST-05). Cada chip
   * desfaz exatamente o gesto que o criou.
   */
  const filtrosAtivos: FiltroAtivo[] = [
    ...(familia ? [{ chave: 'familia',
      texto: FAMILIAS.find((f) => f.codigo === familia)?.nome ?? familia,
      tirar: () => setFamilia('') }] : []),
    ...(categoria ? [{ chave: 'categoria', texto: nomeDaCategoria(categoria),
      tirar: () => setCategoria('') }] : []),
    ...(situacao ? [{ chave: 'situacao',
      texto: SITUACOES.find((x) => x.codigo === situacao)?.nome ?? situacao,
      tirar: () => setSituacao('') }] : []),
    ...(validade ? [{ chave: 'validade',
      texto: VALIDADES.find((x) => x.valor === validade)?.texto ?? validade,
      tirar: () => setValidade('') }] : []),
    ...(soPendencia ? [{ chave: 'travados', texto: 'Só os travados',
      tirar: () => { setSoPendencia(false); setMotivo(null); } }] : []),
    ...(motivo ? [{ chave: 'motivo', texto: `Motivo: ${motivo}`,
      tirar: () => setMotivo(null) }] : []),
    ...(busca.trim() ? [{ chave: 'busca', texto: `«${busca.trim()}»`,
      tirar: () => setBusca('') }] : []),
  ];
  const limparFiltros = () => {
    setFamiliaEstado(''); setValidadeEstado(''); setMotivoEstado(null);
    setCategoria(''); setSituacao(''); setSoPendencia(false); setBusca('');
    /* Uma troca de endereço só: três seguidas pisariam uma na outra. */
    const p = new URLSearchParams(window.location.search);
    ['familia', 'validade', 'motivo'].forEach((k) => p.delete(k));
    setParametros(p, { replace: true });
  };

  /*
   * A leitura da plaqueta (`0053`). A plaqueta carrega SÓ o código
   * patrimonial; quem resolve o código é o sistema, e ele exige conta.
   */
  if (lendoQr) {
    return (
      <LeitorQr
        aoLer={(codigo) => {
          setLendoQr(false);
          ir(`/equipamento/codigo/${encodeURIComponent(codigo)}`);
        }}
        aoCancelar={() => setLendoQr(false)}
      />
    );
  }

  const textoNovo = ePadrao ? 'Novo padrão' : 'Novo equipamento';

  return (
    <div className="parque">
      {/*
        * ─── O topo (onda 206) ─────────────────────────────────────────
        *
        * «A planilha e o layout de equipamentos e calibração estão muito
        *  toscos, parecem amador» — Humberto, 22/09/2026. A captura
        * mostrava por quê: sete botões do mesmo peso esticados na largura
        * inteira (a regra `.linha > *` da casa dá `flex: 1` a todo
        * filho), a busca espremida entre eles, e o «Novo equipamento»
        * competindo com «Categorias» como se fossem a mesma coisa.
        *
        * Agora há hierarquia: o título à esquerda; à direita, UMA ação
        * principal e as ferramentas em botões discretos do tamanho do
        * texto. A busca desce para a barra de filtros, que é onde ela é
        * filtro.
        */}
      <div className="pq-topo">
        <div className="cabeca pq-titulo">
          <h1>Equipamentos e calibração</h1>
          <p className="sub">
            {ePadrao
              ? 'Padrões de referência — os instrumentos contra os quais o laboratório afere os seus'
              : 'Rastreabilidade metrológica ligada ao ensaio — máquinas, instrumentos, itens calibrados e padrões'}
          </p>
        </div>
        <div className="pq-acoes">
          {/*
            * Onda 350 · o topo enxuto. Ficam a leitura da plaqueta (o gesto
            * da bancada, com o celular na mão) e a ação primária; o que se
            * usa uma vez por mês — plaquetas em folha, o relatório, o
            * catálogo — vai para o menu «⋯», o mesmo da OS (P-UI-33). As
            * Pendências moram no cartão dos travados, que é a pergunta
            * delas.
            */}
          <div className="pq-ferramentas" role="group" aria-label="Ferramentas do parque">
            <button type="button" className="pq-ferramenta" data-icone="⌖"
              onClick={() => setLendoQr(true)}>
              Ler plaqueta
            </button>
          </div>
          <MenuDaOs rotulo="Mais ferramentas do parque">
            <LinkProtegido
              className="item-menu"
              caminho={`/equipamentos/plaquetas?papel=${papel}`}
              nomeQueda="plaquetas.html"
            >
              Imprimir plaquetas
            </LinkProtegido>
            {/* O relatório do parque (onda 206, `0194`), com o recorte da tela. */}
            <LinkProtegido
              className="item-menu"
              caminho={caminhoRelatorio}
              nomeQueda={`${ePadrao ? 'padroes' : 'parque-equipamentos'}.pdf`}
            >
              Relatório do parque (PDF)
            </LinkProtegido>
            <Link className="item-menu" to="/equipamentos/pendencias">
              Pendências da metrologia
            </Link>
            {/* Só quem administra: a categoria é a lista que todo o parque
                usa como referência (Testesolo, 23/08/2026). */}
            {pode('cadastro.categoria') && (
              <Link className="item-menu" to="/categorias-de-equipamento">
                Categorias
              </Link>
            )}
          </MenuDaOs>
          {pode('equipamento.editar') ? (
            <button
              type="button"
              className="botao pq-botao"
              onClick={() => setCriando(true)}
            >
              {textoNovo}
            </button>
          ) : (
            /* UX-068: desativado com o motivo, como «Nova OS». */
            <AcaoBloqueada
              texto={textoNovo}
              oQueFaz="não cadastra equipamento"
              permissao="Cadastrar e editar equipamentos"
              persona={eu?.atuandoPor ? eu.nome : null}
            />
          )}
        </div>
      </div>

      {criando && (
        <FormEquipamento
          papel={ePadrao ? 'padrao' : 'equipamento'}
          /* O catálogo agora traz as categorias dos dois papéis; o
             formulário oferece só as do papel que vai cadastrar. */
          categorias={categorias.filter((c) =>
            (c.papel ?? 'equipamento') === (ePadrao ? 'padrao' : 'equipamento'))}
          aoFechar={() => setCriando(false)}
          aoPronto={(id) => ir(`/equipamento/${id}`)}
        />
      )}

      {/*
        * ─── Os indicadores ─────────────────────────────────────────────
        *
        * Quatro números que respondem, antes de qualquer clique, a
        * pergunta de quem abre esta tela de manhã: «está tudo em ordem?».
        * O de impedidos é um botão — ele liga o mesmo corte de «Só o que
        * está travado», e por isso diz o mesmo número.
        */}
      {lista && (
        <div className="pq-indicadores" aria-label="Situação do recorte">
          <div className="pq-ind">
            <span className="pq-ind-n">{indicadores.total}</span>
            <span className="pq-ind-r">
              {ePadrao ? 'padrões' : 'itens'} {filtrando || familia || situacao
                ? 'no recorte' : 'no parque'}
            </span>
          </div>
          <div className="pq-ind ok">
            <span className="pq-ind-n">{indicadores.aptos}</span>
            <span className="pq-ind-r">aptos para ensaio</span>
          </div>
          <button
            type="button"
            className={`pq-ind atencao${ordem === 'vencimento' ? ' ligado' : ''}`}
            aria-pressed={ordem === 'vencimento'}
            title="Ordenar pelo vencimento mais próximo"
            onClick={() => setOrdem(ordem === 'vencimento' ? 'codigo' : 'vencimento')}
          >
            <span className="pq-ind-n">{indicadores.vencendo}</span>
            <span className="pq-ind-r">validade nos próximos {DIAS_ALERTA} dias</span>
          </button>
          <div className={`pq-ind critico pq-ind-motivos${soPendencia ? ' ligado' : ''}`}>
            <button
              type="button"
              className="pq-ind-alvo"
              aria-pressed={soPendencia}
              title="Mostrar só os que não podem entrar em ensaio"
              onClick={() => { setSoPendencia(!soPendencia); setMotivo(null); }}
            >
              <span className="pq-ind-n">{impedidos}</span>
              <span className="pq-ind-r">sem poder entrar em ensaio</span>
            </button>
            {/* Onda 350: o porquê, em números — e cada motivo filtra. */}
            {motivos.length > 0 && (
              <div className="pq-motivos" role="group" aria-label="Por que não podem entrar em ensaio">
                {motivos.map((m) => (
                  <button
                    key={m.texto}
                    type="button"
                    className="chip pq-motivo"
                    aria-pressed={motivo === m.texto}
                    onClick={() => setMotivo(motivo === m.texto ? null : m.texto)}
                  >
                    <b>{m.n}</b> {m.texto}
                  </button>
                ))}
              </div>
            )}
            <Link className="pq-ind-link" to="/equipamentos/pendencias">
              Abrir as pendências
            </Link>
          </div>
        </div>
      )}

      {/*
        * ─── A barra de filtros ─────────────────────────────────────────
        *
        * Um bloco só: a busca, as abas de família (o primeiro corte —
        * «estou procurando uma MÁQUINA ou uma CÁPSULA?»), a visualização,
        * a ordem e o corte de travados. Antes eram três faixas soltas de
        * botões grandes, cada uma com o seu alinhamento.
        */}
      <div className="pq-barra">
        <div className="barra-lista pq-linha">
          <input
            className="pq-busca"
            type="search"
            value={busca}
            onChange={(e) => setBusca(e.target.value)}
            placeholder="Buscar por código, nome, fabricante ou nº de série"
            aria-label="Buscar equipamento"
          />
          <div className="escolha pq-segmento" role="group" aria-label="Visualização">
            <button
              type="button"
              className={modo === 'grupo' ? 'ativo' : undefined}
              aria-pressed={modo === 'grupo'}
              onClick={() => setModo('grupo')}
            >
              Agrupado
            </button>
            <button
              type="button"
              className={modo === 'lista' ? 'ativo' : undefined}
              aria-pressed={modo === 'lista'}
              onClick={() => setModo('lista')}
            >
              Lista
            </button>
          </div>
          {/* «Quero que possa ordenar por ordem alfabética também, tanto
              em lista quanto em agrupado (…) Incluir "quantidade de
              pendências"» — Humberto, 01/09/2026. */}
          <select
            className="filtro-suspenso"
            aria-label="Ordenar por"
            value={ordem}
            onChange={(e) => setOrdem(e.currentTarget.value)}
          >
            <option value="codigo">Ordenar por código</option>
            <option value="nome">Ordenar por nome (A–Z)</option>
            <option value="vencimento">Vencimento mais próximo</option>
            <option value="pendencias">Mais pendências primeiro</option>
          </select>
          {/* Onda 350: a faixa de validade (RN-271), no servidor. */}
          <Seletor
            naBarra
            rotulo="Validade"
            todos="Qualquer validade"
            valor={validade || null}
            opcoes={VALIDADES}
            aoMudar={(v) => setValidade(v ?? '')}
          />
          <button
            type="button"
            className={`pq-alternar${soPendencia ? ' ligado' : ''}`}
            aria-pressed={soPendencia}
            onClick={() => setSoPendencia(!soPendencia)}
          >
            Só o que está travado
            {impedidos > 0 && <span className="pq-contagem"> {impedidos}</span>}
          </button>
        </div>

        {(familiasPresentes.length > 1 || modo === 'lista') && (
          <div className="pq-linha pq-linha-2">
            {familiasPresentes.length > 1 && (
              <div className="pq-abas" role="group" aria-label="Família">
                <button
                  type="button"
                  className={familia === '' ? 'ativa' : undefined}
                  aria-pressed={familia === ''}
                  onClick={() => setFamilia('')}
                >
                  Tudo <span className="pq-contagem">{totalNoCatalogo}</span>
                </button>
                {familiasPresentes.map((f) => (
                  <button
                    key={f.codigo}
                    type="button"
                    className={familia === f.codigo ? 'ativa' : undefined}
                    aria-pressed={familia === f.codigo}
                    title={f.sub}
                    onClick={() => trocarFamilia(f.codigo)}
                  >
                    {f.nome} <span className="pq-contagem">{f.quantos}</span>
                  </button>
                ))}
              </div>
            )}
            {/* Os filtros suspensos só na lista: no agrupado a categoria JÁ
                é a estrutura, e filtrar por ela seria o mesmo gesto duas
                vezes. */}
            {modo === 'lista' && (
              <div className="pq-suspensos">
                <Seletor
                  naBarra
                  rotulo="Categoria"
                  todos="Todas as categorias"
                  valor={categoria || null}
                  opcoes={comItem.map((c) => ({ valor: c.codigo, texto: `${c.nome} (${c.quantos})` }))}
                  aoMudar={(v) => setCategoria(v ?? '')}
                />
                <Seletor
                  naBarra
                  rotulo="Situação"
                  todos="Qualquer situação"
                  valor={situacao || null}
                  opcoes={SITUACOES.map((x) => ({ valor: x.codigo, texto: x.nome }))}
                  aoMudar={(v) => setSituacao(v ?? '')}
                />
              </div>
            )}
          </div>
        )}
        {familia !== '' && (
          <p className="pq-dica">{FAMILIAS.find((f) => f.codigo === familia)?.sub}</p>
        )}
      </div>

      {/* Onda 350: o recorte em chips, com «N de M» (P-LST-05). */}
      <FiltrosAtivos
        itens={filtrosAtivos}
        aoLimpar={limparFiltros}
        contagem={lista && filtrosAtivos.length > 0
          ? `${visiveis.length} de ${totalNoCatalogo || visiveis.length}` : undefined}
      />

      <ErroDaApi erro={erro} />

      {lista === null && !erro && <Carregando texto="Carregando equipamentos" />}

      {lista && visiveis.length === 0 && (
        <Vazio>
          {filtrosAtivos.length > 0
            ? 'Nenhum registro com esse filtro.'
            : ePadrao
              ? 'Nenhum padrão cadastrado. Sem padrão, a calibração interna '
                + 'não rastreia (RN-160). Use “Novo padrão”, acima.'
              : 'Nenhum equipamento cadastrado ainda. Use “Novo equipamento”, '
                + 'acima, para registrar o primeiro.'}
        </Vazio>
      )}

      {lista && visiveis.length > 0 && (
        <div className="cartao pq-cartao">
          <div className="cartao-cabeca">
            <h2>{ePadrao ? 'Padrões de referência' : 'Parque de equipamentos'}</h2>
            <span className="sub">
              {ePadrao ? plural(visiveis.length, 'padrão', 'padrões')
                : plural(visiveis.length, 'equipamento', 'equipamentos')}
              {' em '}{plural(grupos.length, 'categoria', 'categorias')}
              {travadosVisiveis > 0 && ` · ${travadosVisiveis} sem poder entrar em ensaio`}
            </span>
            {modo === 'grupo' && !filtrando && grupos.length > 1 && (
              <button type="button" className="pq-alternar" onClick={abrirOuFecharTodas}>
                {todasAbertas ? 'Recolher todas' : 'Abrir todas'}
              </button>
            )}
          </div>

          {modo === 'grupo' && (
            <div className="pq-grupos">
              {grupos.map((g, i) => (
                <Fragment key={g.codigo}>
                {/* Onda 350: a família como separador — «Máquinas»,
                    «Instrumentos»… —, com a contagem dela no recorte. */}
                {(i === 0 || grupos[i - 1].familia !== g.familia) && (
                  <h3 className="pq-familia">
                    {FAMILIAS.find((f) => f.codigo === g.familia)?.nome ?? rotulo(g.familia)}
                    <span className="pq-contagem">
                      {grupos.filter((x) => x.familia === g.familia)
                        .reduce((n, x) => n + x.itens.length, 0)}
                    </span>
                  </h3>
                )}
                <GrupoDeCategoria
                  grupo={g}
                  aberto={aberta(g.codigo)}
                  aoAlternar={() => alternar(g.codigo)}
                  coluna={colunaDoMeio(g.familia)}
                  podeAferir={pode('equipamento.verificar')}
                  sensoresDe={sensoresDe}
                  comSensores={comSensores}
                  aoAlternarSensores={alternarSensores}
                  aoApagar={() => setApagados((n) => n + 1)}
                  ir={ir}
                />
                </Fragment>
              ))}
            </div>
          )}

          {modo === 'lista' && (
            <ListaDoParque
              itens={visiveis}
              nomeDaCategoria={nomeDaCategoria}
              escolhidos={escolhidos}
              aoEscolher={setEscolhidos}
              papel={papel}
              aoMudar={() => setRecarga((n) => n + 1)}
              aoApagar={() => setApagados((n) => n + 1)}
              ir={ir}
            />
          )}

          <p className="rodape-regra">
            Equipamento fora de tolerância, bloqueado ou com calibração vencida
            não é oferecido em nenhum ensaio <Rn codigo="RN-151"
              diz="Ensaio não seleciona equipamento sem calibração externa ou interna válida." />.
            A exceção exige autorização do Responsável Técnico com justificativa
            auditável <Rn codigo="RN-143"
              diz="Uso de equipamento não conforme exige autorização nominal e justificativa." />.
          </p>
        </div>
      )}
    </div>
  );
}


// ── o grupo de uma categoria ─────────────────────────────────────────

interface Grupo {
  codigo: string;
  nome: string;
  familia: string;
  itens: Equipamento[];
  travados: number;
}

/**
 * Uma categoria, recolhida.
 *
 * O cabeçalho carrega só nome e contagem. «Ficou ruim a parte direita
 * onde diz a data de vencimento e X equipamentos travados. Essas
 * informações deveriam aparecer apenas na visualização lista»
 * (Testesolo, 01/09/2026) — e o vencimento de um grupo de sete balanças é
 * o vencimento de UMA delas, sem dizer qual.
 *
 * O que a onda 206 acrescenta é só uma FAIXA fina sob o nome, com a
 * proporção de aptos: ela não é número nem data, e não tem dono a
 * confundir — diz «este conjunto está inteiro» ou «tem vermelho aqui»
 * sem abrir, e com a palavra no `title` para quem não enxerga a cor.
 */
function GrupoDeCategoria({
  grupo, aberto, aoAlternar, coluna, podeAferir, sensoresDe, comSensores,
  aoAlternarSensores, aoApagar, ir,
}: {
  grupo: Grupo;
  aberto: boolean;
  aoAlternar(): void;
  coluna: 'faixa' | 'local';
  podeAferir: boolean;
  sensoresDe: Map<string, Sensor[]>;
  comSensores: Set<string>;
  aoAlternarSensores(id: string): void;
  aoApagar(): void;
  ir(rota: string): void;
}) {
  const comLixeira = useLixeira();
  const n = grupo.itens.length;
  const aptos = n - grupo.travados;
  /* Onda 350: categoria grande pagina de 50 em 50 (as 222 cápsulas),
     com o rodapé do padrão de lista (P-LST-08). */
  const pag = usePaginacao(grupo.itens, 50);
  return (
    <section className={`grupo-categoria${aberto ? ' aberto' : ''}`}>
      <h3>
        <button type="button" className="abrir" onClick={aoAlternar}
          aria-expanded={aberto}>
          <span className="seta" aria-hidden="true">{aberto ? '▾' : '▸'}</span>
          <span className="pq-grupo-nome">{grupo.nome}</span>
          <span className="quantos">
            {n} {n === 1 ? 'item' : 'itens'}
          </span>
          <span
            className="pq-proporcao"
            title={`${aptos} de ${n} aptos para ensaio`}
            aria-hidden="true"
          >
            <span style={{ width: `${n ? (aptos / n) * 100 : 0}%` }} />
          </span>
          {/* Onda 350: a proporção dita, para quem não enxerga a cor. */}
          <span className="so-leitor">{aptos} de {n} aptos para ensaio</span>
        </button>
      </h3>

      {aberto && (
        <div className="pq-grupo-corpo">
          {/* «Quero imprimir as etiquetas de identificação de todas as
              prensas de adensamento» (Testesolo, 01/09/2026): o pedido
              chega por CONJUNTO, e é o conjunto que está aberto. */}
          <div className="barra-do-grupo">
            {/*
              * A aferição em lote do conjunto (onda 208).
              *
              * «Preciso de uma forma de edição rápida de uma categoria,
              *  principalmente para aferições internas» — Humberto,
              * 22/09/2026. A folha já existia (0107/0123) e só se chegava
              * a ela pela ficha de UM item ou pela tela de verificações:
              * quem abre o conjunto das cápsulas é quem quer pesá-las, e o
              * botão fica onde o conjunto está aberto.
              */}
            {grupo.familia === 'item_aferido' && podeAferir && (
              <Link
                className="pq-aferir"
                data-icone="⚖"
                to={`/equipamentos/aferir-massas?categoria=${encodeURIComponent(grupo.codigo)}`}
              >
                Calibração interna em lote
              </Link>
            )}
            <LinkProtegido
              caminho={`/equipamentos/plaquetas?categoria=${encodeURIComponent(grupo.codigo)}`}
              nomeQueda={`plaquetas-${grupo.codigo}.html`}
            >
              Etiquetas deste conjunto · folha A4
            </LinkProtegido>
            <LinkProtegido
              caminho={`/equipamentos/plaquetas?categoria=${encodeURIComponent(grupo.codigo)}&formato=rolo&pdf=1`}
              nomeQueda={`etiquetas-50x30-${grupo.codigo}.pdf`}
            >
              Etiquetas deste conjunto · 50 × 30 mm
            </LinkProtegido>
            <LinkProtegido
              caminho={`/equipamentos/relatorio-parque?categoria=${encodeURIComponent(grupo.codigo)}&papel=${grupo.familia === 'padrao' ? 'padrao' : 'equipamento'}&pdf=1`}
              nomeQueda={`relatorio-${grupo.codigo}.pdf`}
            >
              Relatório deste conjunto (PDF)
            </LinkProtegido>
          </div>
          <div className="rola">
          <table className="dados clicavel pq-tabela">
            <thead>
              <tr>
                <th>Código</th>
                <th>Equipamento</th>
                <th className="col-opcional">Nº de série</th>
                <th className="col-opcional">
                  {coluna === 'faixa' ? 'Faixa / resolução' : 'Local'}
                </th>
                <th className="col-opcional">Fabricação</th>
                <th className="col-opcional">Última calibração</th>
                <th>Validade</th>
                <th>Situação</th>
                {comLixeira && <th className="col-lixeira" aria-label="Excluir" />}
              </tr>
            </thead>
            <tbody>
              {pag.visiveis.map((e) => {
                const meus = sensoresDe.get(e.id) ?? [];
                const abertoSensor = comSensores.has(e.id);
                return (
                  <Fragment key={e.id}>
                    {/*
                      * A linha NÃO é um link: `role` sobre `<tr>` apaga a
                      * semântica de linha, e há um botão de sensores aqui
                      * dentro. O clique na linha fica, por comodidade de
                      * mouse; quem navega por teclado tem o código.
                      */}
                    <tr
                      className={e.impedimento
                        ? 'linha-alerta linha-navegavel'
                        : 'linha-navegavel'}
                      onClick={() => ir(`/equipamento/${e.id}`)}
                    >
                      <td className="pq-cod">
                        <button
                          type="button"
                          className="link-linha mono"
                          onClick={(k) => { k.stopPropagation(); ir(`/equipamento/${e.id}`); }}
                        >
                          {e.codigo_patrimonial}
                          <span className="so-leitor"> — abrir a ficha</span>
                        </button>
                        {/* A seta dos sensores não navega: abrir os
                            sensores e abrir a ficha são duas intenções. */}
                        {meus.length > 0 && (
                          <button
                            type="button"
                            className="abrir-sensores"
                            aria-expanded={abertoSensor}
                            onClick={(k) => { k.stopPropagation(); aoAlternarSensores(e.id); }}
                          >
                            {abertoSensor ? '▾' : '▸'} {meus.length} sensor(es)
                          </button>
                        )}
                      </td>
                      <td>
                        <div className="pq-nome">{e.nome}</div>
                        {(e.fabricante || e.modelo) && (
                          <div className="sub">
                            {[e.fabricante, e.modelo].filter(Boolean).join(' · ')}
                          </div>
                        )}
                      </td>
                      <td className="col-opcional mono">
                        {e.numero_serie ?? <span className="nada">—</span>}
                      </td>
                      <td className="col-opcional mono">
                        {(coluna === 'faixa'
                          ? faixaDe(e as unknown as Record<string, unknown>)
                          : e.localizacao)
                          || <span className="nada">—</span>}
                      </td>
                      <td className="col-opcional mono">
                        {mesAno(e.data_fabricacao) || <span className="nada">—</span>}
                      </td>
                      <td className="col-opcional"><Ultima e={e} /></td>
                      <td>
                        <Validade
                          ate={e.calibracao_ate ?? (e.ultima_calibracao ? null : e.afericao_ate)}
                          dias={e.dias_para_vencer}
                        />
                      </td>
                      <td><Situacao e={e} /></td>
                      {comLixeira && (
                        <td className="col-lixeira">
                          <Lixeira tipo="equipamento" id={e.id}
                            rotulo={e.codigo_patrimonial} aoApagar={aoApagar} />
                        </td>
                      )}
                    </tr>

                    {abertoSensor && meus.map((c) => (
                      <tr key={c.id}
                        className="linha-sensor linha-navegavel"
                        onClick={() => ir(`/equipamento/${c.id}`)}
                      >
                        <td className="pq-cod">
                          <span className="risco" aria-hidden="true">└</span>
                          <button
                            type="button"
                            className="link-linha mono"
                            onClick={(k) => { k.stopPropagation(); ir(`/equipamento/${c.id}`); }}
                          >
                            {c.codigo_patrimonial}
                            <span className="so-leitor"> — abrir a ficha do sensor</span>
                          </button>
                        </td>
                        <td>
                          <div className="pq-nome">{c.nome}</div>
                          {c.grandeza_medida && (
                            <div className="sub">mede {rotulo(c.grandeza_medida)}</div>
                          )}
                        </td>
                        <td className="col-opcional mono">
                          {c.numero_serie ?? <span className="nada">—</span>}
                        </td>
                        <td className="col-opcional mono">
                          {c.faixa_min !== null && c.faixa_max !== null
                            ? `${numero(c.faixa_min, 2, true)} a `
                              + `${numero(c.faixa_max, 2, true)} ${c.faixa_unidade ?? ''}`
                            : <span className="nada">—</span>}
                        </td>
                        <td className="col-opcional"><span className="nada">—</span></td>
                        <td className="col-opcional mono">
                          {c.ultima_calibracao
                            ? data(c.ultima_calibracao)
                            : <span className="nada">sem registro</span>}
                        </td>
                        <td>
                          <Validade ate={c.calibracao_ate} dias={c.dias_para_vencer} />
                        </td>
                        <td>
                          {c.impedimento
                            ? <Selo tom="critico">{c.impedimento}</Selo>
                            : <Selo tom="ok">Calibrado</Selo>}
                        </td>
                        {comLixeira && (
                          <td className="col-lixeira">
                            <Lixeira tipo="equipamento" id={c.id}
                              rotulo={c.codigo_patrimonial} aoApagar={aoApagar} />
                          </td>
                        )}
                      </tr>
                    ))}
                  </Fragment>
                );
              })}
            </tbody>
          </table>
          </div>
          {n > 50 && (
            <RodapeDaLista pagina={pag.pagina} paginas={pag.paginas} setPagina={pag.setPagina}
              total={pag.total} porPagina={pag.porPagina} acumula={pag.acumula} ordem="código" />
          )}
        </div>
      )}
    </section>
  );
}


// ── a visualização em lista ──────────────────────────────────────────

/**
 * A lista plana, com seleção.
 *
 * ─── Por que ela existe ao lado do agrupado ───────────────────────────
 *
 * O agrupado responde «o que temos deste tipo?». Esta responde «quais são
 * estes, e o que eu faço com eles» — e é aqui que aparecem o número de
 * série, o vencimento de CADA linha, e as caixas que permitem agir sobre
 * itens escolhidos a dedo.
 *
 * ─── A seleção sobrevive ao filtro ────────────────────────────────────
 *
 * Quem escolhe três prensas, troca o filtro para cápsulas e escolhe duas,
 * fica com cinco selecionados. Limpar a seleção ao filtrar pareceria
 * arrumação e seria perda de trabalho: montar uma seleção que atravessa
 * filtros é justamente o que o filtro serve para fazer.
 *
 * Por isso a barra de ações diz quantos estão escolhidos ao todo, e não
 * quantos estão visíveis.
 */
function ListaDoParque({
  itens, nomeDaCategoria, escolhidos, aoEscolher, papel, aoMudar, aoApagar, ir,
}: {
  itens: Equipamento[];
  nomeDaCategoria(c: string): string;
  escolhidos: Set<string>;
  aoEscolher(s: Set<string>): void;
  papel: string;
  aoMudar(): void;
  aoApagar(): void;
  ir(rota: string): void;
}) {
  const [classificando, setClassificando] = useState(false);
  const comLixeira = useLixeira();

  const alternar = (id: string) => {
    const s2 = new Set(escolhidos);
    if (s2.has(id)) s2.delete(id); else s2.add(id);
    aoEscolher(s2);
  };

  /*
   * «Todos» age sobre os VISÍVEIS, e não sobre o parque.
   *
   * Uma caixa no cabeçalho que marcasse os 217 depois de a pessoa ter
   * filtrado sete seria a tela desfazendo o filtro que ela acabou de
   * fazer. Ela marca o que está na tela, que é o que a pessoa está
   * olhando.
   */
  const visiveis = itens.map((e) => e.id);
  const todosMarcados = visiveis.length > 0
    && visiveis.every((id) => escolhidos.has(id));
  const alternarTodos = () => {
    const s2 = new Set(escolhidos);
    if (todosMarcados) for (const id of visiveis) s2.delete(id);
    else for (const id of visiveis) s2.add(id);
    aoEscolher(s2);
  };

  return (
    <>
      {escolhidos.size > 0 && (
        <div className="barra-selecao">
          <b>{escolhidos.size} escolhido(s)</b>
          <LinkProtegido
            className="botao"
            caminho={`/equipamentos/plaquetas?papel=${papel}`
              + `&ids=${[...escolhidos].join(',')}`}
            nomeQueda="plaquetas.html"
          >
            Etiquetas em folha A4
          </LinkProtegido>
          {/* O rolo térmico: mesma seleção, mesmo QR, outro portador. */}
          <LinkProtegido
            className="botao"
            caminho={`/equipamentos/plaquetas?papel=${papel}`
              + `&ids=${[...escolhidos].join(',')}&formato=rolo&pdf=1`}
            nomeQueda="etiquetas-50x30.pdf"
          >
            Etiquetas 50 × 30 mm
          </LinkProtegido>
          <button type="button" className="botao"
            onClick={() => setClassificando(true)}>
            Mudar a situação
          </button>
          <button type="button" className="botao discreto"
            onClick={() => aoEscolher(new Set())}>
            Limpar seleção
          </button>
        </div>
      )}

      {classificando && (
        <FormSituacaoEmLote
          ids={[...escolhidos]}
          aoFechar={() => setClassificando(false)}
          aoPronto={() => { setClassificando(false); aoEscolher(new Set()); aoMudar(); }}
        />
      )}

      <div className="rola">
        <table className="dados pq-tabela">
          <thead>
            <tr>
              <th className="col-escolha">
                <input
                  type="checkbox"
                  checked={todosMarcados}
                  onChange={alternarTodos}
                  aria-label="Escolher todos os visíveis"
                />
              </th>
              <th>Equipamento</th>
              <th className="col-opcional">Número de série</th>
              <th className="col-opcional">Categoria</th>
              <th className="col-opcional">Fabricação</th>
              <th className="col-opcional">Última calibração</th>
              <th>Validade</th>
              <th>Situação</th>
              {comLixeira && <th className="col-lixeira" aria-label="Excluir" />}
            </tr>
          </thead>
          <tbody>
            {itens.map((e) => (
              <tr key={e.id} className={e.impedimento ? 'linha-alerta' : undefined}>
                <td className="col-escolha">
                  <input
                    type="checkbox"
                    checked={escolhidos.has(e.id)}
                    onChange={() => alternar(e.id)}
                    aria-label={`Escolher ${e.codigo_patrimonial}`}
                  />
                </td>
                {/*
                  * A linha inteira não navega mais, e o código sim.
                  *
                  * Com caixa de seleção na linha, um clique em qualquer
                  * lugar levando à ficha faria a pessoa sair da tela ao
                  * errar o alvo da caixa por dois pixels — e perder a
                  * seleção que estava montando.
                  */}
                <td>
                  <button type="button" className="como-link mono"
                    onClick={() => ir(`/equipamento/${e.id}`)}>
                    <b>{e.codigo_patrimonial}</b>
                  </button>
                  <div className="sub">
                    {e.nome}
                    {e.fabricante && ` · ${e.fabricante}`}
                    {e.modelo && ` ${e.modelo}`}
                  </div>
                </td>
                <td className="col-opcional mono">
                  {e.numero_serie ?? <span className="nada">—</span>}
                </td>
                <td className="col-opcional">{nomeDaCategoria(e.categoria)}</td>
                <td className="col-opcional mono">
                  {mesAno(e.data_fabricacao) || <span className="nada">—</span>}
                </td>
                <td className="col-opcional"><Ultima e={e} /></td>
                <td>
                  <Validade
                    ate={e.calibracao_ate ?? (e.ultima_calibracao ? null : e.afericao_ate)}
                    dias={e.dias_para_vencer}
                  />
                </td>
                <td><Situacao e={e} /></td>
                {comLixeira && (
                  <td className="col-lixeira">
                    <Lixeira tipo="equipamento" id={e.id}
                      rotulo={e.codigo_patrimonial} aoApagar={aoApagar} />
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
}

/**
 * Mudar a situação dos escolhidos.
 *
 * O motivo é obrigatório pela mesma razão de sempre: uma prensa que sai
 * de serviço sem dizer por quê é uma prensa que alguém vai religar sem
 * saber o que aconteceu.
 *
 * E a tela mostra QUEM FICOU DE FORA. O servidor recusa mudar o que está
 * em calibração, em verificação ou fora de tolerância — três situações
 * que não são escolha de ninguém, porque vêm de um certificado. Uma
 * operação em lote que responde «pronto» sem dizer quantos é como
 * alguém descobre, semanas depois, que dois equipamentos continuaram em
 * serviço.
 */
function FormSituacaoEmLote({ ids, aoFechar, aoPronto }: {
  ids: string[]; aoFechar(): void; aoPronto(): void;
}) {
  const [status, setStatus] = useState('em_manutencao');
  const [motivo, setMotivo] = useState('');
  const [resultado, setResultado] = useState<
    { mudados: Equipamento[]; parados: Equipamento[] } | null>(null);

  if (resultado) {
    return (
      <Dialogo aberto titulo="O que mudou" aoFechar={aoPronto}>
        <p className="sub">
          {resultado.mudados.length} de {ids.length} mudaram para{' '}
          <b>{rotulo(status)}</b>.
        </p>
        {resultado.parados.length > 0 && (
          <Aviso tipo="atencao" titulo="Estes não mudaram">
            <ul>
              {resultado.parados.map((p) => (
                <li key={p.id}>
                  <b className="mono">{p.codigo_patrimonial}</b>
                  {' — '}{rotulo(p.status)}
                </li>
              ))}
            </ul>
            Situação que vem de certificado ou de calibração interna não se troca à
            mão: mudá-la aqui seria contradizer o papel.
          </Aviso>
        )}
        <div className="acoes">
          <button type="button" className="botao principal" onClick={aoPronto}>
            Entendi
          </button>
        </div>
      </Dialogo>
    );
  }

  return (
    <Form
      titulo={`Mudar a situação de ${ids.length} equipamento(s)`}
      aoFechar={aoFechar}
      rotuloEnviar="Mudar a situação"
      dica="A mudança fica na trilha de cada equipamento, com o motivo."
      enviar={async () => {
        setResultado(await api('/equipamentos/situacao-em-lote', {
          metodo: 'PATCH',
          corpo: { ids, status, motivo: motivo.trim() },
        }));
      }}
    >
      <Escolha
        rotulo="Nova situação"
        valor={status}
        limpavel={false}
        aoEscolher={(v) => setStatus(v ?? 'em_manutencao')}
        opcoes={[
          { valor: 'disponivel', texto: 'Disponível' },
          { valor: 'reservado', texto: 'Reservado' },
          { valor: 'em_manutencao', texto: 'Em manutenção' },
          { valor: 'inativo', texto: 'Inativo' },
        ]}
      />
      <Campo
        rotulo="Motivo (obrigatório)"
        value={motivo}
        required
        onChange={(e) => setMotivo(e.currentTarget.value)}
        dica="Uma prensa que sai de serviço sem dizer por quê é uma prensa
          que alguém religa sem saber o que aconteceu."
      />
    </Form>
  );
}
